import HomeProductCard from "./HomeProductCard";
import "./HomeProductSection.css";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { useState, useEffect, useRef } from "react";
import { findProducts } from "../../../Redux/Customers/Product/Action";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AliceCarousel from "react-alice-carousel";
import { ProductRailSkeleton } from "../../../components/Skeleton";

const prettify = (s = "") =>
  s.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const HomeProductSection = ({ section, query, view_all, eyebrow, link }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { customersProduct } = useSelector((store) => store);
  const [products, setProducts] = useState([]);
  const carouselRef = useRef(null);

  const syncActiveIndex = ({ item }) => setActiveIndex(item);
  const handlePrevClick = () => carouselRef.current && carouselRef.current.slidePrev();
  const handleNextClick = () => carouselRef.current && carouselRef.current.slideNext();

  useEffect(() => {
    dispatch(
      findProducts({
        category: `${query}`,
        colors: [],
        sizes: [],
        // No price limits: a hardcoded maxPrice of 10000 used to hide every
        // product over ₹10,000 from the homepage rails.
        minPrice: "",
        maxPrice: "",
        minDiscount: 0,
        sort: "price_low",
        pageNumber: 1,
        pageSize: 10,
        stock: null,
        view_all,
      })
    );
  }, [dispatch, query, view_all]);

  useEffect(() => {
    setProducts(customersProduct.products);
  }, [customersProduct]);

  // Fractional item counts leave part of the next card visible ("peek"). With a
  // whole number the row ended flush at the viewport edge and looked like a
  // static grid — there are no arrows or dots below lg, so the peek is the only
  // cue that the row scrolls. Desktop already did this with 4.5.
  const responsive = {
    0: { items: 2.2, itemsFit: "contain" },
    568: { items: 3.2, itemsFit: "contain" },
    1024: { items: 4.5, itemsFit: "contain" },
  };

  const content = products[`${query}`]?.content || [];
  const hasProducts = content.length > 0;
  // Show the rail's shape while it loads. A section that genuinely has no
  // products still renders nothing once loading finishes.
  const railLoading = !hasProducts && Boolean(customersProduct.loading);

  if (!hasProducts && !railLoading) return null;

  // "View All" only when the category has more than one desktop row's worth
  // (4 cards). With fewer, the rail already shows everything and the link just
  // leads to the same products.
  const totalInCategory = products[`${query}`]?.total ?? content.length;
  const showViewAll = !railLoading && totalInCategory > 4;

  return (
    <div className="mx-auto max-w-7xl px-4 lg:px-8">
      {/* header */}
      <div className="mb-6 flex items-end justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#b28585]">
            {eyebrow || "Wedding Collection"}
          </p>
          <h2 className="font-display text-3xl font-bold text-[#511d00] sm:text-4xl">
            {prettify(section)}
          </h2>
        </div>
        {showViewAll && (
        <button
          onClick={() =>
            navigate(link || `/${query}/${query}/${query}?view_all=true`)
          }
          className="group flex items-center gap-1 text-sm font-semibold text-[#b28585] transition-colors hover:text-[#5e3e3e]"
        >
          View All
          <ChevronRightRoundedIcon className="transition-transform group-hover:translate-x-0.5" />
        </button>
        )}
      </div>

      {/* carousel */}
      <div className="relative">
        {railLoading ? (
          <ProductRailSkeleton count={5} />
        ) : (
        <AliceCarousel
          disableButtonsControls
          disableDotsControls
          mouseTracking
          items={content.slice(0, 10).map((item) => (
            <div key={item._id} className="px-2">
              <HomeProductCard product={item} category={query} view_all />
            </div>
          ))}
          activeIndex={activeIndex}
          responsive={responsive}
          onSlideChanged={syncActiveIndex}
          animationType="fadeout"
          animationDuration={250}
          ref={carouselRef}
        />
        )}

        {/* arrows */}
        <div className="hidden lg:block">
          {!railLoading && activeIndex !== 0 && (
            <button
              onClick={handlePrevClick}
              className="absolute left-0 top-[40%] z-10 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[#eadcc0] bg-white text-[#5e3e3e] shadow-md transition hover:bg-[#f8ecd7]"
            >
              <ChevronLeftRoundedIcon />
            </button>
          )}
          {content.length > 5 && activeIndex < content.length - 4 && (
            <button
              onClick={handleNextClick}
              className="absolute right-0 top-[40%] z-10 flex h-11 w-11 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full border border-[#eadcc0] bg-white text-[#5e3e3e] shadow-md transition hover:bg-[#f8ecd7]"
            >
              <ChevronRightRoundedIcon />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomeProductSection;
